// Página de uma tarefa do Plano de ação — /administrativo/plano-acao/tarefa/:id
//
// Cada tarefa é uma página: cabeçalho, o que fazer, conteúdo especial por tipo (dados do ERP),
// anotações e links. Rota própria (e não modal) porque tabela de 20 linhas + gráficos não cabem
// em modal sem rolagem; assim também dá link direto, "voltar" e recarregar. Contrato: docs/plano-acao.md.
import { useCallback, useEffect, useState } from 'react';
import { Alert, App as AntApp, Button, Card, Empty, Input, Select, Skeleton, Space, Tag, Typography } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import {
  apagarNota, criarNota, ehConflito, ehSemAcesso, erroDe, obterPagina, salvarLink, salvarTarefa, type PaginaObtida,
} from './api';
import { nomeDoPapel, Rotulo } from './comum';
import { PAGINAS } from './paginas';
import {
  frasePermissoes, hojeIso, opcoesStatusTarefa, periodoCurto, podeApagarNota, ROTULO_TAREFA, tarefaAtrasada, temPaginaEspecial, urlValida,
} from './regras';
import { fmtDataBr, fmtMomentoBr } from '../../utils/dataFmt';

const { Title, Text, Paragraph } = Typography;

export default function PaginaTarefa() {
  const { id } = useParams();
  const tarefaId = Number(id);
  const navigate = useNavigate();
  const { message, modal } = AntApp.useApp();
  const hoje = hojeIso();
  const [pg, setPg] = useState<PaginaObtida | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [semAcesso, setSemAcesso] = useState(false);
  const [texto, setTexto] = useState('');
  const [anotando, setAnotando] = useState(false);
  const [tituloLink, setTituloLink] = useState('');
  const [urlLink, setUrlLink] = useState('');
  const [gravandoLink, setGravandoLink] = useState(false);

  const carregar = useCallback(async () => {
    if (!Number.isInteger(tarefaId) || tarefaId <= 0) { setErro('Tarefa inválida.'); return; }
    try { setPg(await obterPagina(tarefaId)); setErro(null); } catch (e) {
      if (ehSemAcesso(e)) setSemAcesso(true); else setErro(erroDe(e));
    }
  }, [tarefaId]);

  useEffect(() => { void carregar(); }, [carregar]);

  const voltar = () => navigate(pg ? `/administrativo/plano-acao?ciclo=${pg.ciclo.id}` : '/administrativo/plano-acao');

  if (semAcesso) return <div style={{ padding: 24 }}><Alert type="warning" showIcon message="Você não tem acesso ao Plano de ação" /></div>;
  if (erro) {
    return (
      <div style={{ padding: 16 }}>
        <Alert type="error" showIcon message="Não foi possível abrir a tarefa" description={erro}
          action={<Space><Button size="small" onClick={() => void carregar()}>Tentar de novo</Button><Button size="small" onClick={voltar}>Voltar</Button></Space>} />
      </div>
    );
  }
  if (!pg) return <div style={{ padding: 16 }}><Skeleton active /></div>;

  const { tarefa, etapa, ciclo, permissoes, pagina } = pg;
  const ligado = ciclo.status === 'ligado';
  const atrasada = tarefaAtrasada(tarefa, hoje, ligado);
  const gestor = !!permissoes.pode_gerir;
  const def = temPaginaEspecial(tarefa) ? PAGINAS[pagina.tipo] : undefined;
  const observadores = tarefa.papeis_observadores.map((p) => nomeDoPapel(pg, p)).join(', ');

  const trocarStatus = async (status: typeof tarefa.status) => {
    try {
      await salvarTarefa(tarefa.id, tarefa.revisao, { status });
      message.success('Situação atualizada.');
    } catch (e) {
      if (ehConflito(e)) message.warning('Outra pessoa mudou esta tarefa antes de você. Atualizei a tela; confira e faça de novo.');
      else message.error(erroDe(e));
    }
    await carregar();
  };

  const anotar = async () => {
    if (!texto.trim()) return;
    setAnotando(true);
    try { await criarNota(tarefa.id, texto.trim()); setTexto(''); message.success('Anotação salva.'); await carregar(); } catch (e) { message.error(erroDe(e)); } finally { setAnotando(false); }
  };
  const apagar = (nid: number) => modal.confirm({
    title: 'Apagar esta anotação?', okText: 'Apagar', cancelText: 'Voltar', okButtonProps: { danger: true },
    onOk: async () => { try { await apagarNota(tarefa.id, nid); await carregar(); } catch (e) { message.error(erroDe(e)); } },
  });
  const gravarLink = async () => {
    if (!tituloLink.trim()) { message.warning('Dê um nome ao link.'); return; }
    if (!urlValida(urlLink)) { message.warning('O endereço precisa começar com http:// ou https://.'); return; }
    setGravandoLink(true);
    try {
      await salvarLink(tarefa.id, tituloLink.trim(), urlLink.trim());
      setTituloLink(''); setUrlLink(''); message.success('Link salvo.'); await carregar();
    } catch (e) { message.error(erroDe(e)); } finally { setGravandoLink(false); }
  };

  const opcoes = opcoesStatusTarefa(gestor, tarefa.status).map((v) => ({ value: v, label: ROTULO_TAREFA[v] }));
  const notas = [...pg.notas].sort((a, b) => String(b.em).localeCompare(String(a.em)));

  const conteudo = () => {
    if (!temPaginaEspecial(tarefa)) return null;
    if (!def) return <Alert type="warning" showIcon message="Esta tela ainda não conhece esse tipo de página" description="Recarregue com Ctrl+F5. Se continuar, avise o Lucas." />;
    if (!pagina.pode_ver) {
      return (
        <Alert type="warning" showIcon message="Sem acesso a esta página"
          description={pagina.faltam.length ? `Para ver este conteúdo é preciso poder ${frasePermissoes(pagina.faltam)}.` : 'Você não tem a permissão necessária para ver este conteúdo.'} />
      );
    }
    const Comp = def.componente;
    return <Comp tarefa={tarefa} config={pagina.config} podeVer={pagina.pode_ver} podeEditar={pagina.pode_editar} hoje={hoje} />;
  };

  return (
    <div style={{ padding: 16, display: 'grid', gap: 16 }}>
      <div>
        <Button type="link" style={{ padding: 0 }} onClick={voltar}>← Plano de ação</Button>
        <div style={{ color: '#8c8c8c', fontSize: 13 }}>
          {ciclo.titulo} › Semana {etapa.numero} ({periodoCurto(etapa.inicio, etapa.fim)}) · {etapa.titulo}
        </div>
        <Title level={3} style={{ margin: '4px 0 8px' }}>{tarefa.titulo}</Title>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
          <div><Rotulo>Responsável</Rotulo>{nomeDoPapel(pg, tarefa.papel_responsavel)}</div>
          {observadores && <div><Rotulo>Observadores</Rotulo>{observadores}</div>}
          <div>
            <Rotulo>Prazo</Rotulo>
            {atrasada ? <Text type="danger" strong>{fmtDataBr(tarefa.prazo)} (atrasada)</Text> : fmtDataBr(tarefa.prazo)}
          </div>
          <div>
            <Rotulo>Situação</Rotulo>
            {permissoes.pode_mudar_status ? (
              <Select
                style={{ width: 170 }} showSearch optionFilterProp="label" options={opcoes} value={tarefa.status}
                onChange={(v) => { void trocarStatus(v); }}
              />
            ) : <Tag>{ROTULO_TAREFA[tarefa.status]}</Tag>}
          </div>
          {tarefa.concluida_em && (
            <div><Rotulo>Concluída</Rotulo>{fmtMomentoBr(tarefa.concluida_em)}{tarefa.concluida_por ? ` por ${tarefa.concluida_por}` : ''}</div>
          )}
        </div>
        {!ligado && <Alert style={{ marginTop: 8 }} type="info" showIcon message="O ciclo ainda não foi ligado: ninguém foi avisado e o prazo ainda não conta atraso." />}
      </div>

      {tarefa.descricao && (
        <Card size="small" title="O que fazer"><Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{tarefa.descricao}</Paragraph></Card>
      )}

      {conteudo()}

      <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
        <Card size="small" title="Anotações">
          {permissoes.pode_anotar && (
            <div style={{ display: 'grid', gap: 6, marginBottom: 12 }}>
              <Input.TextArea rows={2} maxLength={4000} value={texto} placeholder="Escreva o que ficou combinado ou descoberto" onChange={(ev) => setTexto(ev.target.value)} />
              <div><Button type="primary" size="small" loading={anotando} disabled={!texto.trim()} onClick={() => void anotar()}>Anotar</Button></div>
            </div>
          )}
          {!notas.length && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nenhuma anotação ainda." />}
          {notas.map((n) => (
            <div key={n.id} style={{ borderTop: '1px solid #f0f0f0', padding: '8px 0' }}>
              <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 2 }}>{n.texto}</Paragraph>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {n.por_nome || n.por} · {fmtMomentoBr(n.em)}
                {podeApagarNota(gestor, permissoes.email, n) && <Button type="link" size="small" danger onClick={() => apagar(n.id)}>Apagar</Button>}
              </Text>
            </div>
          ))}
        </Card>

        <Card size="small" title="Links">
          {permissoes.pode_anotar && (
            <div style={{ display: 'grid', gap: 6, marginBottom: 12 }}>
              <Input value={tituloLink} maxLength={200} placeholder="Nome do link (ex.: Planilha no SharePoint)" onChange={(ev) => setTituloLink(ev.target.value)} />
              <Input value={urlLink} maxLength={1000} placeholder="https://…" onChange={(ev) => setUrlLink(ev.target.value)} />
              <div><Button size="small" loading={gravandoLink} onClick={() => void gravarLink()}>Adicionar link</Button></div>
            </div>
          )}
          {!pg.links.length && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nenhum link ainda." />}
          {pg.links.map((l) => (
            <div key={l.id} style={{ borderTop: '1px solid #f0f0f0', padding: '8px 0' }}>
              <a href={l.url} target="_blank" rel="noopener noreferrer">{l.titulo}</a>
              <div><Text type="secondary" style={{ fontSize: 12 }}>{l.por_nome || l.por} · {fmtMomentoBr(l.em)}</Text></div>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}
